import React, { useState, useEffect } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import ReactApexChart from "react-apexcharts";
import axios from "axios";
import config from '../../config/config';
import "../../styles/JobAnalytics.css";
import { Helmet } from "react-helmet";
import moment from "moment";

// Colors for charts
const COLORS = ["#6bb729", "#ffd01d", "#bb081a", "#509ab0"];

const JobAnalytics = () => {
    const [jobData, setJobData] = useState([]);
    const [machineData, setMachineData] = useState([]);
    const [chartData, setChartData] = useState({
        series: [],
        options: {}  // Ensure it's an object, not undefined
    });

    const [loading, setLoading] = useState(true);
    const [radialBarOptions, setRadialBarOptions] = useState({
        series: [],
        options: null
    });


    useEffect(() => {
        fetchJobData();
    }, []);

    const fetchJobData = async () => {
        try {
            const response = await axios.get(`${config.API_BASE_URL}/api/triggerMonitoring`);
            setJobData(response.data.jobs);
            setMachineData(response.data.machines);

            updateRadialBarChart(response.data.jobs);
        } catch (error) {
            console.error("Error fetching job data:", error);
        } finally {
            setLoading(false);
        }
    };

    const updateRadialBarChart = (jobs) => {
        const jobSummary = [
            { name: "Successful", value: jobs.filter(job => job.state === "Successful").length },
            { name: "Faulted", value: jobs.filter(job => job.state === "Faulted").length },
            { name: "Stopped", value: jobs.filter(job => job.state === "Stopped").length }
        ];

        setRadialBarOptions({
            series: jobSummary.map(job => job.value),
            options: {
                chart: {
                    height: 390,
                    type: 'radialBar',
                },
                plotOptions: {
                    radialBar: {
                        offsetY: 0,
                        startAngle: 0,
                        endAngle: 270,
                        hollow: {
                            margin: 5,
                            size: '30%',
                            background: 'transparent',
                        },
                        dataLabels: {
                            name: {
                                show: true,  //  Show category name
                                offsetY: -10, // Adjust placement above bars

                            },
                            value: {
                                show: true,  //  Show job count
                                fontSize: '12px',
                                fontWeight: 'bold',
                                offsetY: 5,  // Adjust value position
                            },
                            total: {
                                show: true,
                                label: 'Total Jobs',
                                fontSize: '12px',  //  Make "Total Jobs" text smaller
                                color: '#666',  // Optional: Change color
                                fontWeight: 'normal', // Optional: Adjust weight
                                formatter: function (w) {
                                    return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                                }
                            }
                        },
                    }
                },
                colors: ['#6bb729', '#bb081a', '#8067dc'], // Matching previous colors
                labels: jobSummary.map(job => job.name), //  Ensure labels are correctly assigned
                legend: {
                    show: true,
                    position: 'bottom',
                },
                responsive: [{
                    breakpoint: 480,
                    options: {
                        legend: {
                            show: true
                        }
                    }
                }]
            }
        });
    };

    const runningPendingSummary = [
        { name: "Running", value: jobData.filter(job => job.state === "Running").length },
        { name: "Pending", value: jobData.filter(job => job.state === "Pending").length }
    ];

    const runningJobsByRobot = machineData
        .filter(machine => machine.running > 0)
        .map(machine => ({
            robot: machine.hostname || "Unknown",
            jobCount: machine.running,
            runningJobs: Array.isArray(machine.runningJobs) ? machine.runningJobs.join(", ") : "No Jobs"
        }));

    return (
        <div className="analytics-container">
            <Helmet>
                <title>Job Analytics - Autonomie</title>
            </Helmet>
            <h2>Job Analytics</h2>
            {loading ? (
                <p>Loading data...</p>
            ) : (
                <>
                    <div className="chart-container">
                        {/* Jobs History Chart */}
                        <div className="chart-item small-chart">
                            <h3>Jobs History</h3>
                            {radialBarOptions.options ? (
                                <ReactApexChart options={radialBarOptions.options} series={radialBarOptions.series} type="radialBar" height={270} />
                            ) : (
                                <p>Loading chart...</p>
                            )}
                        </div>

                        {/* Running & Pending Jobs Chart */}
                        <div className="chart-item large-chart">
                            <h3>Running & Pending Jobs</h3>
                            <ResponsiveContainer width="100%" height={270}>
                                <PieChart>
                                    <Pie data={runningPendingSummary} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={5} dataKey="value" label>
                                        {runningPendingSummary.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.name === "Running" ? "#7fbb6b" : "#dc6967"} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                    <div className="chart-section">
                        <h3>Running Jobs by Robot</h3>
                        <ResponsiveContainer width="80%" height={200}>
                            <BarChart data={runningJobsByRobot}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="robot" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="jobCount" fill="#8067dc" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {/*  Kept Job Execution Timeline Unchanged */}
                    {/* <div className="chart-section">
                        <h3>Job Execution Timeline</h3>
                        <ReactApexChart options={chartData.options} series={chartData.series} type="rangeBar" height={350} />
                    </div> */}
                </>
            )
            }
        </div >
    );
};

export default JobAnalytics;
